import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { Button, Field, Input, Spinner } from '../components/ui.jsx';

export default function Login() {
  const { signIn, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  // Clear a stale error as soon as the user starts fixing their input. This
  // has to hang off the input handlers, not an effect on the values: a failed
  // attempt blanks the password field, and an effect would treat that as
  // typing and erase the very message it was just given.
  const onIdentifier = (e) => { setIdentifier(e.target.value); setError(null); };
  const onPassword = (e) => { setPassword(e.target.value); setError(null); };

  if (loading) return <Spinner label="Checking your session…" />;
  if (isAuthenticated) return <Navigate to={location.state?.from?.pathname ?? '/'} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setPending(true);
    setError(null);

    try {
      const user = await signIn(identifier.trim(), password);

      // Seeded and reset accounts land on the profile page to set a real one.
      if (user.mustChangePassword) {
        navigate('/profile#password', { replace: true });
        return;
      }
      navigate(location.state?.from?.pathname ?? '/', { replace: true });
    } catch (err) {
      setError(err.message);
      setPassword('');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="login__logo" aria-hidden="true">CH</span>
          <h1 className="login__title">Catchu HRMS</h1>
          <p className="login__subtitle">Sign in to the administration console</p>
        </div>

        {error && <div className="login__error" role="alert">{error}</div>}

        <form onSubmit={submit}>
          <Field label="Email or employee code" required>
            <Input
              type="text"
              value={identifier}
              onChange={onIdentifier}
              placeholder="you@company.com or KCAT-001"
              autoComplete="username"
              autoFocus
              required
            />
          </Field>

          <Field label="Password" required>
            <div style={{ position: 'relative' }}>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={onPassword}
                autoComplete="current-password"
                required
                style={{ paddingRight: 62 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: 11, color: 'var(--ink-muted)', fontFamily: 'inherit',
                }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </Field>

          <Button type="submit" variant="primary" size="lg" loading={pending} style={{ width: '100%', marginTop: 6 }}>
            Sign in
          </Button>
        </form>

        <p className="login__footer">
          Employees should use the Catchu HR mobile app.
        </p>
      </div>
    </div>
  );
}
